import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { Map } from "maplibre-gl";
import {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
  LineString,
  Polygon,
  Position
} from "geojson";

import { CIRCE_RADIUS_SOURCE_ID, ROUTING_SOURCE_ID } from "@/constants/map";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { Dataset } from "@/types/dataset";
import { MapStyleProperties, MarkerPinItem } from "@/types/map";
import {
  AreaInputType,
  CatchmentInputBy,
  ECatchmentInputBy,
  ECatchmentType
} from "@/types/spatial";
import {
  extractCoordinatesFromDatasets,
  getVisibleCatchmentDatasets
} from "@/utils/catchment";
import {
  addPolygonLayer,
  addRoutingLayer,
  removeAllSourceLayerWithPrefix
} from "@/utils/map";
import { addPolygonCatchmentSource } from "@/utils/map/polygon";

import { circle, lineString, lineToPolygon } from "@turf/turf";

type UseCatchmentAreaProps = {
  map: Map;
  isMapLoaded: boolean;
  removeAllMarkers: () => void;
  handleAddPolygonLayer: (
    dataset: Dataset,
    polygonData: FeatureCollection<Geometry>,
    customId?: string
  ) => void;
  handleAddMarkers: (
    data: { name?: string; coordinates: [number, number] | Position }[]
  ) => void;
};

const isValidLineString = (geometry: Geometry | undefined): boolean => {
  return !!(
    geometry &&
    geometry?.type === "LineString" &&
    geometry?.coordinates.length >= 2
  );
};

export const useCatchmentArea = ({
  map,
  isMapLoaded,
  removeAllMarkers,
  handleAddPolygonLayer,
  handleAddMarkers
}: UseCatchmentAreaProps) => {
  const [searchParams] = useSearchParams();
  const isPreviewMode = searchParams.get("preview") === "true";
  const {
    catchmentSettings,
    spatialConfig: { areaInput: spatialAreaInputType },
    catchmentSettingsTemp
  } = useSelector((state: RootState) => state.spatial);

  const {
    showCatchmentPopup,
    showAnalysisResultPopup,
    showComparisonPopup,
    showAnalysisSummaryPopup,
    showSearchLocationPopup,
    isDrawPolygon
  } = useSelector((state: RootState) => state.ui);
  const { isBVTDataForCatchmentEnabled } = useAppFeatures();

  const handleAddCatchmentLayer = async (
    dataset: Dataset,
    polygonData: FeatureCollection<Geometry>
  ) => {
    if (!polygonData || !map) return;

    removeAllMarkers();

    const polygonDataCatchment = {
      type: "FeatureCollection",
      features: polygonData.features.map((feature) => ({
        ...feature,
        geometry: feature.properties?.catchment_geometry,
        properties: {
          ...Object.fromEntries(
            Object.entries(feature.properties || {}).filter(
              ([key]) =>
                !["catchment_geometry", "location_geometry"].includes(key)
            )
          )
        }
      }))
    } as FeatureCollection<Geometry>;

    handleAddPolygonLayer(dataset, polygonDataCatchment);
    handleAddMarkers(
      polygonData.features.map((feature) => ({
        coordinates: feature.properties?.location_geometry
          .coordinates as unknown as [number, number]
      }))
    );
  };

  const handleAddRegionLayer = (
    sourceId: string,
    polygonData:
      | FeatureCollection<Geometry>
      | Feature<Polygon, GeoJsonProperties>,
    styleProperties?: MapStyleProperties
  ) => {
    if (!map) return;
    if (map.getSource(sourceId)) return;
    addPolygonCatchmentSource(map, sourceId, polygonData);
    addPolygonLayer(map, sourceId, styleProperties);
  };

  // TODO: @vudh15 refactor for better naming
  const handleAddIsochroneLayer = (
    sourceId: string,
    polygonData:
      | FeatureCollection<Geometry>
      | Feature<Polygon, GeoJsonProperties>,
    styleProperties?: MapStyleProperties
  ) => {
    if (!map) return;
    if (map.getSource(sourceId)) return;
    addPolygonCatchmentSource(map, sourceId, polygonData);
    addRoutingLayer(map, sourceId, styleProperties);
  };

  const removeCatchmentMapDisplay = () => {
    removeAllMarkers();
    // Remove all region polygon
    removeAllSourceLayerWithPrefix(map, CIRCE_RADIUS_SOURCE_ID, []);
    removeAllSourceLayerWithPrefix(map, ROUTING_SOURCE_ID, []);
  };

  const handleAddCircleRadius = (
    catchmentType: string,
    item: MarkerPinItem,
    radiusData: string[]
  ) => {
    if (catchmentType === ECatchmentType.BY_RADIUS) {
      const center = [item.lng, item.lat];

      removeAllSourceLayerWithPrefix(
        map,
        `${CIRCE_RADIUS_SOURCE_ID}-${item.id}`,
        radiusData?.map((radius) => `${radius}meter`) || []
      );

      const radiusValid = radiusData.filter((item) => item !== " ");
      radiusValid.forEach((radius) => {
        const circleArea = circle(center, Number.parseInt(radius), {
          steps: 64,
          units: "meters"
        });
        handleAddIsochroneLayer(
          `${CIRCE_RADIUS_SOURCE_ID}-${item.id}-${radius}meter`,
          circleArea
        );
      });
    }
  };

  const handleAddCircleRadiusOriginal = (
    catchmentInputBy?: CatchmentInputBy
  ) => {
    const radiusData = catchmentSettings?.radiusData ?? [];
    const catchmentType = catchmentSettings?.catchmentType;
    const isCatchment = spatialAreaInputType?.value === AreaInputType.CATCHMENT;

    if (
      !isMapLoaded ||
      !map ||
      !isCatchment ||
      showCatchmentPopup ||
      showAnalysisResultPopup ||
      showComparisonPopup.isShow ||
      showAnalysisSummaryPopup ||
      showSearchLocationPopup ||
      isPreviewMode
    )
      return;

    removeAllSourceLayerWithPrefix(map, ROUTING_SOURCE_ID, []);
    removeAllSourceLayerWithPrefix(map, CIRCE_RADIUS_SOURCE_ID, []);

    if (catchmentInputBy === ECatchmentInputBy.BY_LOCATION) {
      const coordinates = catchmentSettings?.placeCoordinates ?? [];

      for (const item of coordinates) {
        // Handle add circle radius
        handleAddCircleRadius(catchmentType as string, item, radiusData);
      }
    } else {
      const datasets = catchmentSettings?.datasetConfig?.datasets ?? {};
      const coordinates = extractCoordinatesFromDatasets(
        Object.values(datasets)
      );

      for (const item of coordinates) {
        handleAddCircleRadius(catchmentType as string, item, radiusData);
      }
    }
  };

  const handleAddCircleRadiusTemp = (catchmentInputBy?: CatchmentInputBy) => {
    const radiusDataTemp = catchmentSettingsTemp?.radiusData ?? [];
    const catchmentTypeTemp = catchmentSettingsTemp.catchmentType;
    const isCatchment = spatialAreaInputType?.value === AreaInputType.CATCHMENT;

    if (!isMapLoaded || !map || !isCatchment || !showCatchmentPopup) return;

    removeAllSourceLayerWithPrefix(map, CIRCE_RADIUS_SOURCE_ID, []);

    if (catchmentTypeTemp === ECatchmentType.BY_RADIUS) {
      if (catchmentInputBy === ECatchmentInputBy.BY_LOCATION) {
        const coordinatesTemp = catchmentSettingsTemp.placeCoordinates ?? [];
        for (const item of coordinatesTemp) {
          // Handle add circle radius
          handleAddCircleRadius(
            catchmentSettingsTemp.catchmentType as string,
            item,
            radiusDataTemp
          );
        }
      } else {
        const datasets = catchmentSettingsTemp.datasetConfig?.datasets ?? {};
        const visibleEntries = getVisibleCatchmentDatasets(datasets);
        const coordinatesTemp = extractCoordinatesFromDatasets(visibleEntries);

        for (const item of coordinatesTemp) {
          handleAddCircleRadius(
            catchmentTypeTemp as string,
            item,
            radiusDataTemp
          );
        }
      }
    }
  };

  const handleAddIsochrone = () => {
    const catchmentType = catchmentSettings?.catchmentType;
    const isCatchment = spatialAreaInputType?.value === AreaInputType.CATCHMENT;
    const isochroneData =
      catchmentSettings?.roadAccessConfig?.isochroneData ?? [];
    const isochroneDataMapping = isochroneData.map((item) => item?.data).flat();

    if (
      !isMapLoaded ||
      !map ||
      !isCatchment ||
      catchmentType === ECatchmentType.BY_RADIUS ||
      showCatchmentPopup ||
      showAnalysisResultPopup ||
      showComparisonPopup.isShow ||
      showAnalysisSummaryPopup ||
      showSearchLocationPopup ||
      isPreviewMode
    )
      return;

    removeAllSourceLayerWithPrefix(map, ROUTING_SOURCE_ID, []);

    const isochroneDataValid = isochroneDataMapping
      .map((item) => item?.features)
      .flat();

    isochroneDataValid.forEach((item, index) => {
      if (isValidLineString(item.geometry)) {
        const line = lineString((item.geometry as LineString).coordinates);
        const polygon = lineToPolygon(line);
        handleAddIsochroneLayer(`${ROUTING_SOURCE_ID}-${index}`, {
          type: "FeatureCollection",
          features: [polygon]
        });
      }
    });
  };

  const handleAddIsochroneTemp = () => {
    const catchmentTypeTemp = catchmentSettingsTemp.catchmentType;
    const isCatchment = spatialAreaInputType?.value === AreaInputType.CATCHMENT;
    const isochroneData =
      catchmentSettingsTemp.roadAccessConfig?.isochroneData ?? [];

    const isochroneDataMapping = isochroneData
      .filter((item) => item.data)
      .map((item) => item.data)
      .flat();

    if (
      !isMapLoaded ||
      !map ||
      !isCatchment ||
      catchmentTypeTemp === ECatchmentType.BY_RADIUS ||
      !showCatchmentPopup
    )
      return;
    removeAllSourceLayerWithPrefix(map, ROUTING_SOURCE_ID, []);

    const isochroneDataValid = isochroneDataMapping
      .map((item) => item?.features)
      .flat();

    isochroneDataValid.forEach((item, index) => {
      if (isValidLineString(item.geometry)) {
        const line = lineString((item.geometry as LineString).coordinates);
        const polygon = lineToPolygon(line);
        handleAddIsochroneLayer(`${ROUTING_SOURCE_ID}-${index}`, {
          type: "FeatureCollection",
          features: [polygon]
        });
      }
    });
  };

  useEffect(() => {
    const catchmentType = catchmentSettings?.catchmentType;
    const catchmentTypeTemp = catchmentSettingsTemp.catchmentType;
    if (showCatchmentPopup) {
      removeAllSourceLayerWithPrefix(
        map,
        catchmentTypeTemp === ECatchmentType.BY_RADIUS
          ? ROUTING_SOURCE_ID
          : CIRCE_RADIUS_SOURCE_ID,
        []
      );
    } else {
      removeAllSourceLayerWithPrefix(
        map,
        catchmentType === ECatchmentType.BY_RADIUS
          ? ROUTING_SOURCE_ID
          : CIRCE_RADIUS_SOURCE_ID,
        []
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMapLoaded,
    showCatchmentPopup,
    catchmentSettings?.catchmentType,
    catchmentSettingsTemp.catchmentType
  ]);

  // Syncs the COMMITTED catchment state (catchmentSettings) to map layers.
  // Active when the catchment popup is closed. Dispatches to exactly one
  // handler based on catchmentType. When polygon-draw mode is on (BVT flow),
  // the polygon flow owns the visualization, so both source prefixes are wiped.
  useEffect(() => {
    if (isBVTDataForCatchmentEnabled && isDrawPolygon && map) {
      removeAllSourceLayerWithPrefix(map, CIRCE_RADIUS_SOURCE_ID, []);
      removeAllSourceLayerWithPrefix(map, ROUTING_SOURCE_ID, []);
      return;
    }
    if (catchmentSettings?.catchmentType === ECatchmentType.BY_RADIUS) {
      handleAddCircleRadiusOriginal(catchmentSettings?.catchmentInputBy);
    } else {
      handleAddIsochrone();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMapLoaded,
    showCatchmentPopup,
    spatialAreaInputType,
    catchmentSettings?.catchmentInputBy,
    catchmentSettings?.catchmentType,
    catchmentSettings?.radiusData,
    catchmentSettings?.placeCoordinates,
    catchmentSettings?.datasetConfig?.datasets,
    catchmentSettings?.roadAccessConfig?.isochroneData,
    showAnalysisResultPopup,
    showAnalysisSummaryPopup,
    showComparisonPopup,
    showSearchLocationPopup,
    isPreviewMode,
    isDrawPolygon
  ]);

  // Syncs the DRAFT catchment state (catchmentSettingsTemp) to map layers.
  // Active while the catchment popup is open so the user sees a live preview
  // of edits before committing. Same single-handler dispatch by catchmentType
  // as the committed-state effect above.
  useEffect(() => {
    if (isBVTDataForCatchmentEnabled && isDrawPolygon && map) {
      removeAllSourceLayerWithPrefix(map, CIRCE_RADIUS_SOURCE_ID, []);
      removeAllSourceLayerWithPrefix(map, ROUTING_SOURCE_ID, []);
      return;
    }
    if (catchmentSettingsTemp?.catchmentType === ECatchmentType.BY_RADIUS) {
      handleAddCircleRadiusTemp(catchmentSettingsTemp?.catchmentInputBy);
    } else {
      handleAddIsochroneTemp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMapLoaded,
    showCatchmentPopup,
    spatialAreaInputType,
    catchmentSettingsTemp?.catchmentInputBy,
    catchmentSettingsTemp?.catchmentType,
    catchmentSettingsTemp?.radiusData,
    catchmentSettingsTemp?.placeCoordinates,
    catchmentSettingsTemp.datasetConfig?.datasets,
    catchmentSettingsTemp?.roadAccessConfig?.isochroneData,
    isDrawPolygon
  ]);

  return {
    handleAddIsochroneTemp,
    handleAddCatchmentLayer,
    handleAddRegionLayer,
    handleAddIsochroneLayer,
    removeCatchmentMapDisplay
  };
};

export default useCatchmentArea;
